import { useEffect } from "react";

import {
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
  useReactRouterLink,
} from "../../../hooks";
import {
  IInviteTeamMember,
  IHookForm,
  IModalFormWithTableProps,
  IPageAndSearchRequest,
  PagedMeta,
  IViewProduct,
  IFormWithTableProps,
  IAccessClaim,
  IPermission,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";

import { View } from "./View";
import {
  IInviteTeamFormHelper,
  addTeamMemberValidationSchema,
} from "./validationSchema";

export interface IInviteTeamMemberFormViewProps
  extends IHookForm<IInviteTeamMember>,
    IModalFormWithTableProps {
  isLoadingAvailableAccessClaims: boolean;
  isErrorAvailableAccessClaims: boolean;

  // userAccessClaimsFieldArray: IInviteTeamMember["user_access_claims"];
  appendNewUserAccessClaim: (claim: string) => void;
  removeUserAccessClaim: (claim: string) => void;

  availableAccessClaims: IAccessClaim[];
  formValues: IInviteTeamMember;
}

export const InviteTeamMemberForm = (props: IFormWithTableProps & {}) => {
  const { navigate } = useReactRouterLink();

  const closeModal = () => {
    navigate("/settings/team-and-security");
  };

  const {
    isLoading: isLoadingAvailableAccessClaims,
    isError: isErrorAvailableAccessClaims,
    // isSuccess: isSuccessAvailableAccessClaims,
    data: availableAccessClaims,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    IAccessClaim[],
    PagedMeta
  >([
    "getAvailableAccessClaims",
    {
      page: 1,
      page_size: 500,
      search: null,
    },
  ]);

  const {
    isLoading: isLoadingAvailablePermissions,
    isError: isErrorAvailablePermissions,
    // isSuccess: isSuccessAvailablePermissions,
    data: availablePermissions,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    IPermission[],
    PagedMeta
  >([
    "getAvailablePermissions",
    {
      page: 1,
      page_size: 500,
      search: null,
    },
  ]);

  const { isSuccess: isSuccessInviteTeamMember, mutate: inviteTeamMember } =
    useMerchantApiMutation<IInviteTeamMember, IViewProduct>(
      "inviteTeamMember",
      []
    ).usePostMutation;

  useEffect(() => {
    if (isSuccessInviteTeamMember) {
      props.refreshTable();
      closeModal();
      reset();
    }
  }, [isSuccessInviteTeamMember]);

  const initialFormValues: IInviteTeamMember & IInviteTeamFormHelper = {
    email: "",
    message: null,
    user_access_claims: [],
    auxillary_permissions: [],
    availableAccessClaimsSelectHelper: null,
  };

  const handleApiSubmit = (data: IInviteTeamMember) => {
    inviteTeamMember(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, errors, register, reset, control, setValue, watch } =
    useHookForm({
      initialFormValues,
      validationSchema: addTeamMemberValidationSchema,
      onValid: handleApiSubmit,
      onInvalid: handleError,
    });

  const formValues = watch();

  const nonAddedAccessClaims =
    availableAccessClaims?.filter(
      (claim) =>
        formValues?.user_access_claims &&
        [...formValues.user_access_claims].find((cl) => cl === claim.claim) ===
          undefined
    ) || [];

  const removeUserAccessClaim = (claim: string) => {
    if (window.confirm("Are you sure you want to remove this role?")) {
      var remainingNewUserClaims = formValues.user_access_claims.filter(
        (cl) => cl !== claim
      );

      setValue("user_access_claims", remainingNewUserClaims || []);
    }
  };

  const appendNewUserAccessClaim = (claim: string) => {
    var claimAlreadyAdded =
      formValues.user_access_claims.find((cl) => cl === claim) !== undefined;
    if (claimAlreadyAdded) {
      return;
    }

    setValue("availableAccessClaimsSelectHelper", null);
    setValue("user_access_claims", [...formValues.user_access_claims, claim]);
  };

  return (
    <View
      {...props}
      isModalOpen
      closeModal={closeModal}
      appendNewUserAccessClaim={appendNewUserAccessClaim}
      removeUserAccessClaim={removeUserAccessClaim}
      isLoadingAvailableAccessClaims={isLoadingAvailableAccessClaims}
      isErrorAvailableAccessClaims={isErrorAvailableAccessClaims}
      availableAccessClaims={nonAddedAccessClaims}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
      formValues={formValues}
    />
  );
};
